import React, { useState, useMemo } from 'react';
import * as Yup from 'yup';
import { Alert, Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import allForm from '../../Stateless/Form';
import { COLORS } from '../../../theme/themeConstants';
import { MultiSelectField } from './MultiSelectField';
import { useAllDriverManagement } from '../../Query/DriverManagerQuery';
import { useReportApi } from '../../../hooks/useReportApi';

const validationSchema = Yup.object().shape({
  fromDate: Yup.string().required('From Date is required'),
  toDate: Yup.string().required('To Date is required'),
  driverName: Yup.array().nullable(),
});

const DeliveryPerformanceReport: React.FC = () => {
  const today = useMemo(() => dayjs(), []);

  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(today);
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(today);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const { downloadDeliveryPerformanceReport, loading } = useReportApi();

  // Fetch data from SDK
  const { data: driverData } = useAllDriverManagement({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  } as any);

  const driverNameOptions = useMemo(
    () =>
      (driverData?.data || []).map((item: any) => ({
        value: item.deliverBoyId,
        label:
          `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() ||
          'Unknown Driver',
      })),
    [driverData]
  );

  const formConfig = {
    initialValues: {
      fromDate: today.toISOString(),
      toDate: today.toISOString(),
      driverName: [],
    },
    validationSchema,
    onSubmit: async (values: Record<string, any>) => {
      try {
        setSuccessMessage(null);
        setErrorMessage(null);
        setInfoMessage(null);

        const { driverName } = values;

        const fromDate = selectedFromDate ? selectedFromDate?.toISOString().split('T')[0] + 'T00:00:00Z' : undefined;
        const toDate = selectedToDate ? selectedToDate?.toISOString().split('T')[0] + 'T23:59:59Z' : undefined;

        if (!fromDate || !toDate) {
          setErrorMessage('Both From Date and To Date are required');
          return;
        }

        const request: any = {
          dateRange: 'custom',
          dateFrom: fromDate,
          dateTo: toDate,
        };

        if (Array.isArray(driverName) && driverName.length > 0) {
          request.deliveryPartnerIds = driverName;
        }

        const result = await downloadDeliveryPerformanceReport(request);

        if ((result as any)?.noData) {
          setInfoMessage(
            (result as any).message || 'No delivery performance data found for the selected filters.'
          );
        } else {
          setSuccessMessage('Delivery Performance report download started.');
        }
      } catch (err: any) {
        setErrorMessage(err?.message || 'Failed to download Delivery Performance report.');
      }
    },
    validateOnChange: true,
    enableReinitialize: true,
    fields: [
      {
        name: 'fromDate',
        label: 'From Date:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedFromDate}
              onChange={(newValue: Dayjs | null) => {
                setSelectedFromDate(newValue);
                form.setFieldValue(field.name, newValue ? newValue.toISOString() : '');
              }}
              slotProps={{
                textField: {
                  placeholder: 'MM/DD/YYYY',
                  sx: { width: '300px' },
                },
              }}
            />
          </LocalizationProvider>
        ),
      },
      {
        name: 'toDate',
        label: 'To Date:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedToDate}
              onChange={(newValue: Dayjs | null) => {
                setSelectedToDate(newValue);
                form.setFieldValue(field.name, newValue ? newValue.toISOString() : '');
              }}
              slotProps={{
                textField: {
                  placeholder: 'MM/DD/YYYY',
                  sx: { width: '300px' },
                },
              }}
            />
          </LocalizationProvider>
        ),
      },
      {
        name: 'driverName',
        label: 'Driver Name:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <MultiSelectField
            field={field}
            form={form}
            options={driverNameOptions}
            placeholder="Select Drivers"
            width="300px"
          />
        ),
      },
    ],
    customButton: () => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: COLORS.primary.main,
            textTransform: 'none',
            '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) },
          }}
        >
          {loading ? 'Downloading...' : 'Download'}
        </Button>
        <Button type="reset" variant="outlined" disabled={loading} sx={{color: COLORS.secondary.main, borderColor:COLORS.secondary.main,}}>
          Reset
        </Button>
      </Box>
    ),
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', padding: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, marginBottom: 3, color: '#333', textAlign: 'left' }}
      >
        Delivery Performance Report
      </Typography>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: 900,
          margin: '0 auto',
        }}
      >
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {infoMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {infoMessage}
          </Alert>
        )}
        {allForm(formConfig)()}
      </Box>
    </Box>
  );
};

export default DeliveryPerformanceReport;
